import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Trash2, Image, ListOrdered } from 'lucide-react';
import type { EntityDependency, DependencyCheckResult } from '@littlebooker/dal';

export type SelectedDependencyIds = Record<string, string[]>;

export interface ImageDeletionInfo {
  /** The cover image URL of the entity being deleted. */
  url: string;
  /** True when other shows / a series default also reference this image. */
  isShared: boolean;
  /** Human labels (title or slug) of other shows sharing the image, for display. */
  sharedLabels?: string[];
  /** A series default cover points at this image (future generated episodes use it). */
  usedBySeriesDefault?: boolean;
}

interface DeleteConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    selected?: SelectedDependencyIds,
    options?: { deleteImage?: boolean; renumberEpisodes?: boolean },
  ) => void;
  entityName: string;
  entityType: 'happening' | 'task' | 'person' | 'place' | 'thing' | 'program' | 'template' | 'artist' | 'selection';
  entityCount?: number; // For bulk deletion
  dependencies?: DependencyCheckResult;
  /**
   * Map of dependency type -> default-checked. Listed types are rendered as selectable
   * (per-item checkboxes) instead of read-only. Types not present here remain read-only.
   */
  selectableDependencies?: Record<string, boolean>;
  isDeleting?: boolean;
  mediaUrls?: string[]; // URLs of media files that will be deleted (legacy read-only display)
  /**
   * Cover-image deletion control. When provided, the dialog shows an opt-in checkbox to
   * also delete the image file — disabled when the image is shared, so a single delete
   * can never wipe media used by other episodes. The choice is returned via onConfirm.
   */
  imageDeletion?: ImageDeletionInfo;
  /**
   * Episode-renumbering control for a numbered series instance. Episode numbers are assigned
   * once at generation time, so deleting one from the middle leaves every later episode one
   * number too high; this offers to close the gap in the same action. Defaults to on, because
   * leaving the gap is almost never what you want.
   */
  episodeRenumber?: EpisodeRenumberInfo;
}

export interface EpisodeRenumberInfo {
  /** How many later episodes in the series would shift down. */
  laterEpisodeCount: number;
  /** Episode number of the show being deleted, for the copy. */
  episodeNumber?: number | null;
}

const DEPENDENCY_LABELS: Record<string, string> = {
  tasks: 'Tasks',
  subtasks: 'Subtasks',
  comments: 'Comments',
  participants: 'Artists',
  happening_participants: 'Happening Participants',
  assigned_tasks: 'Assigned Tasks',
  requirements: 'Requirements',
  happening_requirements: 'Happening Requirements',
  child_places: 'Child Places',
  happenings: 'Happenings',
};

export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  entityName,
  entityType,
  entityCount = 1,
  dependencies,
  selectableDependencies,
  isDeleting = false,
  mediaUrls = [],
  imageDeletion,
  episodeRenumber,
}: DeleteConfirmationDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [selected, setSelected] = useState<SelectedDependencyIds>({});
  // Opt-in: default to NOT deleting the image. A shared image can never be opted-in.
  const [deleteImage, setDeleteImage] = useState(false);
  // Opt-out: closing the gap is the sane default.
  const [renumberEpisodes, setRenumberEpisodes] = useState(true);
  const isBulk = entityCount > 1;
  const requiredText = 'delete';

  // Initialize selection state when dialog opens or dependencies change
  useEffect(() => {
    if (!open) {
      setConfirmText('');
      setSelected({});
      setDeleteImage(false);
      setRenumberEpisodes(true);
      return;
    }
    if (!dependencies || !selectableDependencies) return;
    const initial: SelectedDependencyIds = {};
    for (const dep of dependencies.dependencies) {
      if (dep.type in selectableDependencies) {
        initial[dep.type] = selectableDependencies[dep.type]
          ? dep.items.map((i) => i.id)
          : [];
      }
    }
    setSelected(initial);
  }, [open, dependencies, selectableDependencies]);

  const isSelectable = (depType: string) =>
    !!selectableDependencies && depType in selectableDependencies;

  const toggleItem = (depType: string, id: string) => {
    setSelected((prev) => {
      const current = prev[depType] ?? [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { ...prev, [depType]: next };
    });
  };

  const toggleAll = (dep: EntityDependency) => {
    setSelected((prev) => {
      const current = prev[dep.type] ?? [];
      const allIds = dep.items.map((i) => i.id);
      const allSelected = allIds.every((id) => current.includes(id));
      return { ...prev, [dep.type]: allSelected ? [] : allIds };
    });
  };

  const canConfirm = confirmText.toLowerCase() === requiredText && !isDeleting;

  const totalSelectedExtras = useMemo(
    () => Object.values(selected).reduce((sum, ids) => sum + ids.length, 0),
    [selected],
  );

  // A shared image is never deletable through this control.
  const canDeleteImage = !!imageDeletion && !imageDeletion.isShared;
  const effectiveDeleteImage = canDeleteImage && deleteImage;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(
      selectableDependencies ? selected : undefined,
      imageDeletion || episodeRenumber
        ? {
            ...(imageDeletion ? { deleteImage: effectiveDeleteImage } : {}),
            ...(episodeRenumber ? { renumberEpisodes } : {}),
          }
        : undefined,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canConfirm) {
      e.preventDefault();
      handleConfirm();
    }
  };

  const totalDependentItems = dependencies?.dependencies.reduce((sum, dep) => sum + dep.count, 0) ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="medium">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {isBulk ? `Delete ${entityCount} ${entityType}s?` : `Delete ${entityName}?`}
          </DialogTitle>
          <DialogDescription>
            {isBulk
              ? `You are about to delete ${entityCount} ${entityType}s. This action cannot be undone.`
              : `You are about to delete "${entityName}". This action cannot be undone.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-4 sm:px-6 py-4">
          {dependencies?.hasDependencies && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <div className="mb-3 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-destructive mb-1">
                    {selectableDependencies
                      ? `This ${entityType} has ${totalDependentItems} related item${totalDependentItems !== 1 ? 's' : ''}`
                      : `Warning: This will also delete ${totalDependentItems} dependent item${totalDependentItems !== 1 ? 's' : ''}`}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {selectableDependencies
                      ? 'Choose which related items to also delete. Unchecked items will not be deleted (links are removed but the records remain).'
                      : `Deleting this ${entityType} will cause the following items to be deleted as well:`}
                  </p>
                </div>
              </div>
              <div className="space-y-2 mt-3">
                {dependencies.dependencies.map((dep) => {
                  const selectable = isSelectable(dep.type);
                  const selectedIds = selected[dep.type] ?? [];
                  const allChecked =
                    selectable && dep.items.length > 0 && dep.items.every((i) => selectedIds.includes(i.id));
                  return (
                    <div key={dep.type} className="bg-background rounded-md p-3 border">
                      <div className="font-medium text-sm mb-2 flex items-center justify-between gap-2">
                        <span>
                          {DEPENDENCY_LABELS[dep.type] || dep.type} ({dep.count})
                          {selectable && selectedIds.length > 0 && (
                            <span className="text-muted-foreground font-normal ml-1">
                              — {selectedIds.length} to delete
                            </span>
                          )}
                        </span>
                        {selectable && (
                          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={() => toggleAll(dep)}
                              disabled={isDeleting}
                            />
                            Select all
                          </label>
                        )}
                      </div>
                      <ul className="space-y-1 max-h-40 overflow-y-auto">
                        {dep.items.slice(0, selectable ? dep.items.length : 10).map((item) => (
                          <li key={item.id} className="text-sm text-muted-foreground pl-2">
                            {selectable ? (
                              <label className="inline-flex items-start gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={selectedIds.includes(item.id)}
                                  onChange={() => toggleItem(dep.type, item.id)}
                                  disabled={isDeleting}
                                />
                                <span>{item.name}</span>
                              </label>
                            ) : (
                              <>• {item.name}</>
                            )}
                          </li>
                        ))}
                        {!selectable && dep.items.length > 10 && (
                          <li className="text-sm text-muted-foreground pl-2 italic">
                            ... and {dep.items.length - 10} more
                          </li>
                        )}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {episodeRenumber && episodeRenumber.laterEpisodeCount > 0 && (
            <div className="rounded-lg border bg-muted/40 p-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={renumberEpisodes}
                  onChange={(e) => setRenumberEpisodes(e.target.checked)}
                  disabled={isDeleting}
                />
                <span>
                  <span className="font-medium text-sm flex items-center gap-1.5">
                    <ListOrdered className="h-4 w-4" />
                    Also renumber the {episodeRenumber.laterEpisodeCount} later episode
                    {episodeRenumber.laterEpisodeCount !== 1 ? 's' : ''}
                  </span>
                  <span className="block text-sm text-muted-foreground mt-0.5">
                    Episode numbers are assigned when episodes are generated, so deleting
                    {episodeRenumber.episodeNumber ? ` episode ${episodeRenumber.episodeNumber}` : ' this one'}
                    {' '}would otherwise leave every later episode one number too high. Uncheck to keep
                    the existing numbers and the gap.
                  </span>
                </span>
              </label>
            </div>
          )}

          {imageDeletion && (
            <div className="rounded-lg border border-warning-border bg-warning-muted p-4">
              <div className="flex items-start gap-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded border">
                  <img
                    src={imageDeletion.url}
                    alt="Cover"
                    className="h-16 w-16 object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3C/svg%3E';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  {imageDeletion.isShared ? (
                    <>
                      <div className="flex items-center gap-1.5 font-semibold text-warning-foreground">
                        <Image className="h-4 w-4" />
                        Cover image will be kept
                      </div>
                      <p className="mt-1 text-sm text-warning-foreground/80">
                        This image is shared with{' '}
                        {imageDeletion.sharedLabels && imageDeletion.sharedLabels.length > 0
                          ? `${imageDeletion.sharedLabels.length} other show${imageDeletion.sharedLabels.length !== 1 ? 's' : ''}`
                          : 'other shows'}
                        {imageDeletion.usedBySeriesDefault ? ' and the series default' : ''}
                        , so it won&apos;t be deleted.
                      </p>
                      {imageDeletion.sharedLabels && imageDeletion.sharedLabels.length > 0 && (
                        <p className="mt-1 text-xs text-warning-foreground/70 truncate">
                          Used by: {imageDeletion.sharedLabels.slice(0, 5).join(', ')}
                          {imageDeletion.sharedLabels.length > 5 ? `, +${imageDeletion.sharedLabels.length - 5} more` : ''}
                        </p>
                      )}
                    </>
                  ) : (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={deleteImage}
                        onChange={(e) => setDeleteImage(e.target.checked)}
                        disabled={isDeleting}
                      />
                      <span>
                        <span className="font-medium text-sm flex items-center gap-1.5">
                          <Image className="h-4 w-4" />
                          Also delete the cover image
                        </span>
                        <span className="block text-sm text-muted-foreground mt-0.5">
                          This image isn&apos;t used by any other show. Leave unchecked to keep the file
                          in storage (recommended if you might reuse it).
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {!imageDeletion && mediaUrls.length > 0 && (
            <div className="rounded-lg border border-warning-border bg-warning-muted p-4">
              <div className="mb-2 flex items-start gap-2">
                <Image className="h-5 w-5 text-warning-foreground mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-warning-foreground mb-1">
                    {mediaUrls.length} media file{mediaUrls.length !== 1 ? 's' : ''} will be deleted
                  </h4>
                  <p className="text-sm text-warning-foreground/80">
                    The following photo{mediaUrls.length !== 1 ? 's' : ''} will be removed from storage:
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {mediaUrls.slice(0, 6).map((url, idx) => (
                  <div key={idx} className="overflow-hidden rounded border">
                    <img
                      src={url}
                      alt={`Media ${idx + 1}`}
                      className="h-20 w-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                ))}
                {mediaUrls.length > 6 && (
                  <div className="flex items-center justify-center rounded border border-dashed text-xs text-muted-foreground">
                    +{mediaUrls.length - 6} more
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Type <span className="font-mono text-destructive">{requiredText}</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={requiredText}
              className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono"
              autoFocus
              disabled={isDeleting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {isDeleting ? (
              'Deleting...'
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete {isBulk ? `${entityCount} ${entityType}s` : entityName}
                {selectableDependencies && totalSelectedExtras > 0 && (
                  <span className="ml-1">+ {totalSelectedExtras}</span>
                )}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
